/* Scoped to the widget's Shadow DOM. :host resets anything inherited from the page. */
:host {
  all: initial;
  --vs-primary: #111827;
  --vs-primary-fg: #ffffff;
  --vs-primary-rgb: 17 24 39;
  --vs-primary-deep: #0c111b;
  --vs-bg: #ffffff;
  --vs-surface: #f6f7f9;
  --vs-fg: #111827;
  --vs-muted: #6b7280;
  --vs-border: #e7e9ee;
  --vs-radius: 20px;
  --vs-shadow: 0 24px 60px -12px rgb(15 23 42 / 0.28), 0 8px 20px -8px rgb(15 23 42 / 0.18);
  --vs-ease: cubic-bezier(0.22, 1, 0.36, 1);
  --vs-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
  font-family:
    'Inter',
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    sans-serif;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

button {
  font: inherit;
  color: inherit;
}

.vs-root {
  /* Defined here, where the brand colour variables are set, so the tint follows the brand. */
  --vs-primary-soft: rgb(var(--vs-primary-rgb) / 0.1);
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
  color: var(--vs-fg);
  font-size: 14px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

.vs-root[data-position='left'] {
  right: auto;
  left: 20px;
  align-items: flex-start;
}

/* --- Launcher -------------------------------------------------------------------------- */

.vs-launch-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.vs-root[data-position='left'] .vs-launch-row {
  flex-direction: row-reverse;
}

.vs-launcher {
  position: relative;
  width: 60px;
  height: 60px;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--vs-primary), var(--vs-primary-deep));
  color: var(--vs-primary-fg);
  cursor: pointer;
  box-shadow:
    0 10px 28px -6px rgb(var(--vs-primary-rgb) / 0.6),
    0 4px 10px rgb(0 0 0 / 0.12);
  display: grid;
  place-items: center;
  transition:
    transform 220ms var(--vs-spring),
    box-shadow 220ms var(--vs-ease);
}

.vs-launcher:hover {
  transform: scale(1.07);
}

.vs-launcher:active {
  transform: scale(0.95);
}

.vs-launcher-icon {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  transition:
    transform 320ms var(--vs-spring),
    opacity 200ms var(--vs-ease);
}

.vs-icon-close {
  opacity: 0;
  transform: rotate(-90deg) scale(0.6);
}

.is-open .vs-icon-chat {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}

.is-open .vs-icon-close {
  opacity: 1;
  transform: none;
}

/* A soft ring that breathes until the chat is first opened. */
.vs-root:not(.is-open) .vs-launcher::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgb(var(--vs-primary-rgb) / 0.45);
  animation: vs-pulse 2.8s var(--vs-ease) 1.2s 3;
}

.vs-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  border: 2px solid #ffffff;
  animation: vs-pop 360ms var(--vs-spring);
}

/* --- Teaser bubble next to the launcher ------------------------------------------------ */

.vs-teaser {
  position: relative;
  max-width: 250px;
  margin-bottom: 8px;
  animation: vs-rise 420ms var(--vs-ease);
}

.vs-teaser-body {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--vs-border);
  border-radius: 16px 16px 4px 16px;
  background: var(--vs-bg);
  box-shadow: 0 12px 30px -10px rgb(15 23 42 / 0.3);
  cursor: pointer;
  text-align: left;
  font-size: 13.5px;
  transition: transform 200ms var(--vs-ease);
}

.vs-root[data-position='left'] .vs-teaser-body {
  border-radius: 16px 16px 16px 4px;
}

.vs-teaser-body:hover {
  transform: translateY(-2px);
}

.vs-teaser-close {
  position: absolute;
  top: -10px;
  right: -8px;
  width: 22px;
  height: 22px;
  border: 1px solid var(--vs-border);
  border-radius: 50%;
  background: var(--vs-bg);
  color: var(--vs-muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 160ms;
}

.vs-teaser:hover .vs-teaser-close,
.vs-teaser-close:focus-visible {
  opacity: 1;
}

/* --- Panel ----------------------------------------------------------------------------- */

.vs-panel {
  position: relative;
  width: min(390px, calc(100vw - 32px));
  height: min(640px, calc(100vh - 112px));
  background: var(--vs-bg);
  border-radius: var(--vs-radius);
  box-shadow: var(--vs-shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform-origin: bottom right;
  animation: vs-open 360ms var(--vs-ease);
}

.vs-root[data-position='left'] .vs-panel {
  transform-origin: bottom left;
}

.vs-panel.is-closing {
  animation: vs-close 200ms var(--vs-ease) forwards;
  pointer-events: none;
}

.vs-home,
.vs-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  animation: vs-fade 260ms var(--vs-ease);
}

.vs-icon-btn {
  width: 36px;
  height: 36px;
  flex: none;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: inherit;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 160ms;
}

.vs-icon-btn:hover {
  background: rgb(255 255 255 / 0.16);
}

/* --- Home ------------------------------------------------------------------------------ */

.vs-hero {
  position: relative;
  overflow: hidden;
  padding: 18px 22px 64px;
  background: linear-gradient(155deg, var(--vs-primary) 0%, var(--vs-primary-deep) 100%);
  color: var(--vs-primary-fg);
}

.vs-hero-glow {
  position: absolute;
  inset: -40% -30% auto auto;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgb(255 255 255 / 0.28), transparent 65%);
  animation: vs-float 9s ease-in-out infinite alternate;
  pointer-events: none;
}

.vs-hero-top {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 26px;
}

.vs-hero-org {
  position: relative;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  opacity: 0.85;
  animation: vs-rise 420ms var(--vs-ease) 40ms both;
}

.vs-hero-title {
  position: relative;
  margin: 4px 0 8px;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 700;
  letter-spacing: -0.02em;
  animation: vs-rise 460ms var(--vs-ease) 80ms both;
}

.vs-hero-sub {
  position: relative;
  margin: 0;
  font-size: 14px;
  opacity: 0.88;
  animation: vs-rise 500ms var(--vs-ease) 120ms both;
}

.vs-home-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-top: -44px;
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
}

.vs-card {
  width: 100%;
  border: 1px solid var(--vs-border);
  border-radius: 16px;
  background: var(--vs-bg);
  box-shadow: 0 8px 24px -12px rgb(15 23 42 / 0.25);
  animation: vs-rise 520ms var(--vs-ease) both;
}

.vs-card:nth-child(2) {
  animation-delay: 80ms;
}

.vs-start {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  text-align: left;
  cursor: pointer;
  transition:
    transform 220ms var(--vs-ease),
    box-shadow 220ms var(--vs-ease),
    border-color 220ms;
}

.vs-start:hover {
  transform: translateY(-3px);
  border-color: rgb(var(--vs-primary-rgb) / 0.35);
  box-shadow: 0 16px 32px -14px rgb(15 23 42 / 0.35);
}

.vs-card-icon {
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: 14px;
  background: var(--vs-primary-soft);
  color: var(--vs-primary);
  display: grid;
  place-items: center;
}

.vs-card-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.vs-card-title {
  font-weight: 650;
  font-size: 15px;
}

.vs-card-sub {
  color: var(--vs-muted);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vs-card-go {
  color: var(--vs-primary);
  transition: transform 220ms var(--vs-ease);
}

.vs-start:hover .vs-card-go {
  transform: translateX(4px);
}

.vs-faqs {
  padding: 6px 0;
}

.vs-faqs-title {
  padding: 10px 16px 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vs-muted);
}

.vs-faq {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-top: 1px solid var(--vs-border);
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition:
    background 160ms,
    color 160ms;
}

.vs-faq:first-of-type {
  border-top: 0;
}

.vs-faq svg {
  flex: none;
  color: var(--vs-muted);
  transition: transform 200ms var(--vs-ease);
}

.vs-faq:hover {
  background: var(--vs-primary-soft);
  color: var(--vs-primary);
}

.vs-faq:hover svg {
  color: var(--vs-primary);
  transform: translateX(3px);
}

.vs-brand {
  padding: 8px;
  text-align: center;
  font-size: 11px;
  color: var(--vs-muted);
  letter-spacing: 0.01em;
}

.vs-brand strong {
  color: var(--vs-fg);
  font-weight: 650;
}

/* --- Recent chats and the "chat has ended" bar ------------------------------------------- */

.vs-recent-item {
  align-items: center;
}

.vs-recent-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.vs-recent-preview {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.vs-recent-meta {
  font-size: 12px;
  color: var(--vs-muted);
}

.vs-ended {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  animation: vs-rise 300ms var(--vs-ease);
}

.vs-ended-text {
  font-size: 13px;
  color: var(--vs-muted);
}

.vs-ended-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.vs-ended-actions .vs-prechat-submit {
  width: 100%;
}

/* --- Avatars --------------------------------------------------------------------------- */

.vs-avatar {
  position: relative;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 700;
}

.vs-avatar-sm {
  width: 26px;
  height: 26px;
  font-size: 11px;
}

.vs-avatar-md {
  width: 38px;
  height: 38px;
  font-size: 14px;
}

.vs-avatar-lg {
  width: 48px;
  height: 48px;
  font-size: 18px;
  box-shadow: 0 0 0 3px rgb(255 255 255 / 0.25);
}

.vs-avatar-bot {
  background: linear-gradient(145deg, var(--vs-primary), var(--vs-primary-deep));
  color: var(--vs-primary-fg);
}

.vs-hero .vs-avatar-bot,
.vs-header .vs-avatar-bot {
  background: rgb(255 255 255 / 0.18);
  color: var(--vs-primary-fg);
}

.vs-avatar-agent {
  background: #fde68a;
  color: #78350f;
}

.vs-online {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid var(--vs-primary);
}

.vs-online::after {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 2px solid #22c55e;
  animation: vs-ping 2s var(--vs-ease) infinite;
}

/* --- Chat ------------------------------------------------------------------------------ */

.vs-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 10px;
  background: linear-gradient(155deg, var(--vs-primary), var(--vs-primary-deep));
  color: var(--vs-primary-fg);
}

.vs-header-text {
  flex: 1;
  min-width: 0;
}

.vs-title {
  font-size: 15px;
  font-weight: 650;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vs-subtitle {
  font-size: 12px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vs-log {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--vs-surface);
  scroll-behavior: smooth;
}

.vs-log::-webkit-scrollbar {
  width: 6px;
}

.vs-log::-webkit-scrollbar-thumb {
  background: rgb(0 0 0 / 0.14);
  border-radius: 999px;
}

.vs-msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 86%;
  animation: vs-msg-in 320ms var(--vs-ease) both;
}

.vs-msg.vs-first {
  margin-top: 8px;
}

.vs-msg-me {
  align-self: flex-end;
  flex-direction: row-reverse;
  animation-name: vs-msg-in-me;
}

.vs-msg-bot {
  align-self: flex-start;
}

.vs-msg-system {
  align-self: center;
  max-width: 95%;
  text-align: center;
  justify-content: center;
}

.vs-gutter {
  width: 26px;
  flex: none;
  align-self: flex-start;
}

.vs-stack {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vs-msg-me .vs-stack {
  align-items: flex-end;
}

.vs-bubble {
  padding: 10px 14px;
  border-radius: 18px;
  background: var(--vs-bg);
  color: var(--vs-fg);
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.06);
  border: 1px solid var(--vs-border);
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.vs-msg-bot.vs-first .vs-bubble {
  border-top-left-radius: 6px;
}

.vs-msg-me .vs-bubble {
  background: linear-gradient(145deg, var(--vs-primary), var(--vs-primary-deep));
  color: var(--vs-primary-fg);
  border-color: transparent;
  box-shadow: 0 6px 14px -8px rgb(var(--vs-primary-rgb) / 0.7);
}

.vs-msg-me.vs-first .vs-bubble {
  border-top-right-radius: 6px;
}

.vs-bubble p {
  margin: 0;
  white-space: normal;
}

.vs-bubble p + p,
.vs-bubble ul {
  margin-top: 6px;
}

.vs-bubble ul {
  margin: 4px 0 0;
  padding-left: 18px;
  white-space: normal;
}

.vs-bubble a {
  color: inherit;
}

.vs-msg-system .vs-bubble {
  background: transparent;
  border: 0;
  box-shadow: none;
  color: var(--vs-muted);
  font-size: 12.5px;
  padding: 4px 8px;
}

.vs-sender,
.vs-sources,
.vs-time {
  font-size: 11.5px;
  color: var(--vs-muted);
  margin: 3px 6px 0;
}

.vs-sender {
  margin: 0 6px 3px;
  font-weight: 600;
}

.vs-sources a {
  color: var(--vs-primary);
  text-decoration: none;
  font-weight: 500;
}

.vs-sources a:hover {
  text-decoration: underline;
}

.vs-time {
  font-size: 10.5px;
  opacity: 0;
  transition: opacity 160ms;
}

.vs-msg:hover .vs-time,
.vs-msg:last-child .vs-time {
  opacity: 1;
}

.vs-bubble-typing {
  padding: 12px 16px;
}

.vs-typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 14px;
}

.vs-typing i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--vs-muted);
  opacity: 0.5;
  animation: vs-dot 1.2s ease-in-out infinite;
}

.vs-typing i:nth-child(2) {
  animation-delay: 0.15s;
}

.vs-typing i:nth-child(3) {
  animation-delay: 0.3s;
}

.vs-suggestions {
  display: flex;
  gap: 8px;
  padding: 8px 14px;
  overflow-x: auto;
  background: var(--vs-surface);
  scrollbar-width: none;
}

.vs-suggestions::-webkit-scrollbar {
  display: none;
}

.vs-chip {
  flex: none;
  border: 1px solid rgb(var(--vs-primary-rgb) / 0.4);
  color: var(--vs-primary);
  background: var(--vs-bg);
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  animation: vs-rise 420ms var(--vs-ease) both;
  transition:
    background 160ms,
    color 160ms,
    transform 160ms var(--vs-ease);
}

.vs-chip:hover:not(:disabled) {
  background: var(--vs-primary);
  color: var(--vs-primary-fg);
  transform: translateY(-1px);
}

.vs-notice {
  margin: 0 12px 8px;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 13px;
  color: #991b1b;
  background: #fef2f2;
  border: 1px solid #fecaca;
  animation: vs-rise 260ms var(--vs-ease);
}

.vs-composer {
  padding: 10px 12px 0;
  background: var(--vs-bg);
  border-top: 1px solid var(--vs-border);
}

.vs-input-wrap {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 6px 6px 14px;
  border: 1.5px solid var(--vs-border);
  border-radius: 22px;
  background: var(--vs-bg);
  transition:
    border-color 180ms,
    box-shadow 180ms;
}

.vs-input-wrap:focus-within {
  border-color: var(--vs-primary);
  box-shadow: 0 0 0 4px rgb(var(--vs-primary-rgb) / 0.14);
}

.vs-composer textarea {
  flex: 1;
  resize: none;
  border: 0;
  outline: 0;
  padding: 7px 0;
  font: inherit;
  font-size: 14.5px;
  line-height: 1.4;
  color: var(--vs-fg);
  background: transparent;
  max-height: 120px;
}

.vs-composer textarea::placeholder {
  color: #9ca3af;
}

.vs-send {
  width: 36px;
  height: 36px;
  flex: none;
  border: 0;
  border-radius: 50%;
  background: #e5e7eb;
  color: #9ca3af;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition:
    background 200ms,
    color 200ms,
    transform 260ms var(--vs-spring);
}

.vs-send.is-ready {
  background: linear-gradient(145deg, var(--vs-primary), var(--vs-primary-deep));
  color: var(--vs-primary-fg);
  transform: rotate(-12deg) scale(1.04);
}

.vs-send.is-ready:hover {
  transform: rotate(0) scale(1.1);
}

.vs-send:disabled {
  cursor: default;
}

.vs-chip:disabled {
  opacity: 0.5;
  cursor: default;
}

.vs-launcher:focus-visible,
.vs-icon-btn:focus-visible,
.vs-card:focus-visible,
.vs-faq:focus-visible,
.vs-chip:focus-visible,
.vs-send:focus-visible,
.vs-star:focus-visible,
.vs-teaser-body:focus-visible {
  outline: 3px solid rgb(var(--vs-primary-rgb) / 0.5);
  outline-offset: 2px;
}

.vs-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.vs-csat {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 2px;
  padding: 12px 16px;
  border-radius: 16px;
  background: var(--vs-bg);
  border: 1px solid var(--vs-border);
}

.vs-csat-title {
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}

.vs-star {
  border: 0;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  color: #d1d5db;
  cursor: pointer;
  padding: 2px;
  transition:
    color 140ms,
    transform 200ms var(--vs-spring);
}

.vs-star:hover,
.vs-star:focus-visible {
  color: #f59e0b;
  transform: scale(1.25);
}

/* Hovering a star lights up the ones before it too. */
.vs-csat:hover .vs-star {
  color: #f59e0b;
}

.vs-csat .vs-star:hover ~ .vs-star {
  color: #d1d5db;
}

/* --- Follow-up suggestions (one-tap next questions) ---------------------------------------- */

.vs-followups {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin: 6px 0 2px 34px;
  max-width: calc(100% - 34px);
}

.vs-followup {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 8px 12px 8px 14px;
  border: 1px solid rgb(var(--vs-primary-rgb) / 0.3);
  border-radius: 14px;
  background: var(--vs-bg);
  color: var(--vs-primary);
  font-size: 13.5px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  animation: vs-rise 380ms var(--vs-ease) both;
  transition:
    background 160ms,
    color 160ms,
    border-color 160ms,
    transform 180ms var(--vs-ease);
}

.vs-followup span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.vs-followup svg {
  flex: none;
  transition: transform 180ms var(--vs-ease);
}

.vs-followup:hover {
  background: var(--vs-primary);
  border-color: var(--vs-primary);
  color: var(--vs-primary-fg);
  transform: translateX(2px);
}

.vs-followup:hover svg {
  transform: translateX(3px);
}

.vs-followup:focus-visible,
.vs-ask-link:focus-visible {
  outline: 3px solid rgb(var(--vs-primary-rgb) / 0.5);
  outline-offset: 2px;
}

/* --- Asking for details in the chat ---------------------------------------------------------- */

.vs-ask {
  animation: vs-rise 320ms var(--vs-ease);
}

.vs-ask input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  padding: 8px 0;
  font: inherit;
  font-size: 14.5px;
  color: var(--vs-fg);
  background: transparent;
}

.vs-ask .vs-input-wrap {
  border-color: rgb(var(--vs-primary-rgb) / 0.55);
  box-shadow: 0 0 0 4px rgb(var(--vs-primary-rgb) / 0.1);
}

.vs-ask .vs-input-wrap.has-error {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgb(239 68 68 / 0.12);
  animation: vs-shake 320ms var(--vs-ease);
}

.vs-ask-error {
  margin: 6px 4px 0;
  font-size: 12px;
  color: #b91c1c;
}

.vs-ask-actions {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  padding: 6px 6px 0;
}

/* Deliberately dull: always there, never competing with the answer. */
.vs-skip-icon {
  width: 32px;
  height: 32px;
  flex: none;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #9ca3af;
  opacity: 0.55;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition:
    opacity 160ms,
    background 160ms,
    color 160ms;
}

.vs-skip-icon:hover:not(:disabled),
.vs-skip-icon:focus-visible {
  opacity: 1;
  color: var(--vs-muted);
  background: #f3f4f6;
}

.vs-skip-icon:focus-visible {
  outline: 3px solid rgb(var(--vs-primary-rgb) / 0.5);
  outline-offset: 1px;
}

.vs-ask-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  padding: 2px 0;
  font-size: 12.5px;
  color: var(--vs-muted);
  cursor: pointer;
}

.vs-ask-link:hover {
  color: var(--vs-fg);
  text-decoration: underline;
}

@keyframes vs-shake {
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}

/* --- Avatar image ------------------------------------------------------------------------ */

/* The logo's own background colour is set inline (Assistant → Logo background). */
.vs-avatar.has-img {
  overflow: visible;
  box-shadow: inset 0 0 0 1px rgb(15 23 42 / 0.08);
}

/* The whole logo, never cropped, with a little breathing room inside the circle. */
.vs-avatar img {
  width: 100%;
  height: 100%;
  padding: 14%;
  border-radius: 50%;
  object-fit: contain;
}

/* Home screen: a plate that fits square logos and grows sideways for wide wordmarks. */
.vs-logo-plate {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  min-width: 52px;
  max-width: 200px;
  padding: 8px 12px;
  border-radius: 16px;
  box-shadow:
    0 0 0 3px rgb(255 255 255 / 0.22),
    0 6px 16px -8px rgb(0 0 0 / 0.35);
  animation: vs-rise 420ms var(--vs-ease);
}

.vs-logo-plate img {
  display: block;
  height: 100%;
  width: auto;
  max-width: 176px;
  object-fit: contain;
}

.vs-logo-plate .vs-online {
  right: -3px;
  bottom: -3px;
}

/* --- Pre-chat form --------------------------------------------------------------------- */

.vs-log-form {
  gap: 12px;
}

.vs-prechat {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: 34px;
  padding: 16px;
  border: 1px solid var(--vs-border);
  border-radius: 18px;
  background: var(--vs-bg);
  box-shadow: 0 8px 24px -14px rgb(15 23 42 / 0.25);
  animation: vs-rise 420ms var(--vs-ease) 120ms both;
}

.vs-prechat-title {
  font-weight: 650;
  font-size: 14.5px;
}

.vs-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  animation: vs-rise 380ms var(--vs-ease) both;
}

.vs-field label {
  font-size: 12.5px;
  font-weight: 600;
}

.vs-optional {
  color: var(--vs-muted);
  font-weight: 400;
}

.vs-field input {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid var(--vs-border);
  border-radius: 12px;
  font: inherit;
  font-size: 14px;
  color: var(--vs-fg);
  background: var(--vs-bg);
  outline: 0;
  transition:
    border-color 160ms,
    box-shadow 160ms;
}

.vs-field input:focus {
  border-color: var(--vs-primary);
  box-shadow: 0 0 0 4px rgb(var(--vs-primary-rgb) / 0.14);
}

.vs-field input[aria-invalid='true'] {
  border-color: #ef4444;
}

.vs-field-error {
  font-size: 12px;
  color: #b91c1c;
}

.vs-prechat-submit {
  margin-top: 2px;
  padding: 11px 16px;
  border: 0;
  border-radius: 12px;
  background: linear-gradient(145deg, var(--vs-primary), var(--vs-primary-deep));
  color: var(--vs-primary-fg);
  font-weight: 650;
  cursor: pointer;
  transition:
    transform 200ms var(--vs-spring),
    opacity 160ms;
}

.vs-prechat-submit:hover:not(:disabled) {
  transform: translateY(-1px);
}

.vs-prechat-submit:disabled {
  opacity: 0.6;
  cursor: default;
}

.vs-prechat-submit:focus-visible {
  outline: 3px solid rgb(var(--vs-primary-rgb) / 0.5);
  outline-offset: 2px;
}

.vs-prechat-note {
  margin: 0;
  font-size: 11.5px;
  color: var(--vs-muted);
  text-align: center;
}

/* --- Phones: the panel takes the whole screen ------------------------------------------ */

@media (max-width: 480px) {
  .vs-root.is-open {
    inset: 0;
    gap: 0;
  }

  .vs-root.is-open .vs-panel {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    animation-name: vs-open-mobile;
  }

  .vs-root.is-open .vs-panel.is-closing {
    animation-name: vs-close-mobile;
  }

  .vs-root.is-open .vs-launch-row {
    display: none;
  }

  .vs-hero-title {
    font-size: 26px;
  }

  .vs-composer textarea,
  .vs-field input,
  .vs-ask input {
    font-size: 16px; /* stops iOS zooming into the field */
  }

  .vs-composer {
    padding-bottom: env(safe-area-inset-bottom);
  }
}

/* --- Motion ---------------------------------------------------------------------------- */

@keyframes vs-open {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.94);
  }
}

@keyframes vs-close {
  to {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }
}

@keyframes vs-open-mobile {
  from {
    transform: translateY(100%);
  }
}

@keyframes vs-close-mobile {
  to {
    transform: translateY(100%);
  }
}

@keyframes vs-fade {
  from {
    opacity: 0;
  }
}

@keyframes vs-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
}

@keyframes vs-msg-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
    transform-origin: left bottom;
  }
}

@keyframes vs-msg-in-me {
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.98);
  }
}

@keyframes vs-dot {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

@keyframes vs-pulse {
  0% {
    box-shadow: 0 0 0 0 rgb(var(--vs-primary-rgb) / 0.45);
  }

  70% {
    box-shadow: 0 0 0 16px transparent;
  }

  100% {
    box-shadow: 0 0 0 0 transparent;
  }
}

@keyframes vs-ping {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }

  80%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

@keyframes vs-pop {
  from {
    transform: scale(0);
  }
}

@keyframes vs-float {
  from {
    transform: translate(0, 0);
  }

  to {
    transform: translate(-30px, 20px);
  }
}

/* --- Voice notes ------------------------------------------------------------------------ */

.vs-mic {
  color: var(--vs-muted);
}

.vs-mic:not(:disabled):hover {
  background: var(--vs-primary-soft);
  color: var(--vs-primary);
}

.vs-note {
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 48px;
  padding: 6px 6px 6px 8px;
  border: 1px solid var(--vs-border);
  border-radius: 24px;
  background: var(--vs-bg);
  animation: vs-fade 200ms var(--vs-ease);
}

.vs-note-btn {
  display: grid;
  flex: none;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--vs-muted);
  cursor: pointer;
  transition:
    background 140ms,
    color 140ms;
}

.vs-note-btn:hover {
  background: var(--vs-surface);
  color: var(--vs-fg);
}

.vs-note-play {
  background: var(--vs-primary-soft);
  color: var(--vs-primary);
}

.vs-note-rec {
  width: 10px;
  height: 10px;
  flex: none;
  border-radius: 50%;
  background: #e5484d;
  animation: vs-note-blink 1.2s ease-in-out infinite;
}

@keyframes vs-note-blink {
  50% {
    opacity: 0.25;
  }
}

.vs-note-label {
  flex: 1;
  color: var(--vs-fg);
  font-size: 13.5px;
  font-weight: 500;
}

.vs-note-track {
  position: relative;
  flex: 1;
  height: 4px;
  min-width: 40px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--vs-border);
}

.vs-note-track span {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: inherit;
  background: currentColor;
}

.vs-note-time {
  flex: none;
  color: var(--vs-muted);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.vs-send.vs-note-stop {
  background: #e5484d;
  color: #ffffff;
}

.vs-bubble-note {
  min-width: 210px;
}

.vs-note-player {
  display: flex;
  gap: 8px;
  align-items: center;
  color: inherit;
}

.vs-msg-me .vs-note-player .vs-note-play {
  background: rgb(255 255 255 / 0.2);
  color: inherit;
}

.vs-msg-me .vs-note-track {
  background: rgb(255 255 255 / 0.3);
}

.vs-msg-me .vs-note-time {
  color: inherit;
  opacity: 0.8;
}

.vs-note-text {
  margin-top: 6px;
  font-size: 13px;
  opacity: 0.9;
}

.vs-note-text:empty {
  display: none;
}

/* Rating an answer: quiet until hovered or used. */
.vs-rate {
  display: flex;
  gap: 2px;
  align-items: center;
  margin-top: 2px;
  color: var(--vs-muted);
}

.vs-rate-btn {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  opacity: 0.55;
  cursor: pointer;
  transition:
    opacity 120ms,
    background 120ms,
    transform 160ms var(--vs-spring);
}

.vs-rate-btn:hover {
  background: var(--vs-surface);
  opacity: 1;
}

.vs-rate-btn:active {
  transform: scale(0.9);
}

.vs-rate-btn.is-on {
  color: var(--vs-primary);
  opacity: 1;
}

.vs-rate-btn:focus-visible {
  outline: 2px solid var(--vs-primary);
  outline-offset: 1px;
  opacity: 1;
}

.vs-rate-thanks {
  margin-right: 4px;
  font-size: 11px;
  animation: vs-fade 260ms var(--vs-ease);
}

/* --- Voice call ------------------------------------------------------------------------ */

.vs-chat {
  position: relative;
}

.vs-voice {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 22px 22px calc(22px + env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  background: linear-gradient(165deg, var(--vs-primary) 0%, var(--vs-primary-deep) 100%);
  color: var(--vs-primary-fg);
  animation: vs-fade 260ms var(--vs-ease);
}

.vs-voice-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
}

.vs-voice-name {
  font-size: 17px;
  font-weight: 700;
}

.vs-voice-status {
  font-size: 13px;
  opacity: 0.8;
}

.vs-voice-time {
  font-variant-numeric: tabular-nums;
}

/* The orb grows with the visitor's voice (--vs-level) and pulses while the assistant talks. */
.vs-orb {
  --vs-level: 0;
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 168px;
  height: 168px;
  margin: auto 0;
}

.vs-orb-ring {
  position: absolute;
  inset: 18px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.12;
  transform: scale(calc(1 + var(--vs-level) * 0.35));
  transition: transform 90ms linear;
}

.vs-orb-ring-2 {
  inset: 34px;
  opacity: 0.16;
  transform: scale(calc(1 + var(--vs-level) * 0.2));
}

.vs-orb-core {
  position: relative;
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
  overflow: hidden;
  border-radius: 50%;
  background: rgb(255 255 255 / 0.16);
  box-shadow: 0 10px 30px -8px rgb(0 0 0 / 0.35);
}

.vs-orb-core .vs-logo-plate {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.vs-voice.is-speaking .vs-orb-ring {
  animation: vs-orb-talk 1.2s var(--vs-ease) infinite;
}

.vs-voice.is-speaking .vs-orb-ring-2 {
  animation-delay: 0.3s;
}

.vs-voice.is-thinking .vs-orb-core,
.vs-voice.is-connecting .vs-orb-core {
  animation: vs-orb-wait 1.4s ease-in-out infinite;
}

.vs-voice.is-ended .vs-orb {
  opacity: 0.55;
}

@keyframes vs-orb-talk {
  0% {
    transform: scale(1);
    opacity: 0.2;
  }

  70% {
    transform: scale(1.3);
    opacity: 0;
  }

  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

@keyframes vs-orb-wait {
  50% {
    transform: scale(0.94);
    opacity: 0.7;
  }
}

.vs-voice-captions {
  width: 100%;
  max-width: 320px;
  min-height: 66px;
  text-align: center;
}

.vs-voice-captions p {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  font-size: 15px;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.vs-voice-user,
.vs-voice-hint {
  opacity: 0.75;
}

.vs-voice-actions {
  display: flex;
  gap: 22px;
  align-items: center;
  justify-content: center;
}

.vs-voice-btn {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border: 0;
  border-radius: 50%;
  background: rgb(255 255 255 / 0.18);
  color: inherit;
  cursor: pointer;
  transition:
    transform 160ms var(--vs-spring),
    background 160ms;
}

.vs-voice-btn:hover:not(:disabled) {
  transform: scale(1.06);
}

.vs-voice-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.vs-voice-btn.is-on {
  background: #ffffff;
  color: #111827;
}

.vs-voice-end {
  width: 64px;
  height: 64px;
  background: #e5484d;
  color: #ffffff;
}

.vs-voice-btn:focus-visible,
.vs-voice-back:focus-visible {
  outline: 3px solid rgb(255 255 255 / 0.7);
  outline-offset: 3px;
}

.vs-voice-back {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 12px 22px;
  border: 0;
  border-radius: 999px;
  background: #ffffff;
  color: #111827;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: transform 160ms var(--vs-spring);
}

.vs-voice-back:hover {
  transform: scale(1.04);
}

.vs-voice-after {
  flex-direction: column;
  gap: 10px;
}

.vs-voice-link {
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 500;
  opacity: 0.85;
  cursor: pointer;
}

.vs-voice-link:hover {
  opacity: 1;
  text-decoration: underline;
}

.vs-voice-link:focus-visible {
  outline: 3px solid rgb(255 255 255 / 0.7);
  outline-offset: 3px;
}

.vs-voice-tag {
  display: inline-flex;
  gap: 3px;
  align-items: center;
  vertical-align: -1px;
}

.vs-voice-card .vs-card-icon {
  background: var(--vs-primary);
  color: var(--vs-primary-fg);
}

.vs-card:disabled {
  opacity: 0.6;
  cursor: default;
}

@media (max-height: 560px) {
  .vs-orb {
    width: 120px;
    height: 120px;
  }

  .vs-orb-core {
    width: 72px;
    height: 72px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 1ms !important;
  }
}

/* --- Rich message parts: quick replies, free times, cards --------------------------------- */

.vs-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.vs-slots {
  margin-top: 8px;
  padding: 10px;
  border: 1px solid var(--vs-border);
  border-radius: 14px;
  background: var(--vs-bg);
  animation: vs-rise 420ms var(--vs-ease) both;
}

.vs-slot-days {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  margin-bottom: 8px;
}

.vs-slot-days::-webkit-scrollbar {
  display: none;
}

.vs-slot-day {
  flex: none;
  border: 1px solid var(--vs-border);
  background: var(--vs-surface);
  color: var(--vs-fg);
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
}

.vs-slot-day.is-on {
  background: var(--vs-primary);
  border-color: var(--vs-primary);
  color: var(--vs-primary-fg);
}

.vs-slot-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--vs-muted);
  margin-bottom: 8px;
}

.vs-slot-times {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(78px, 1fr));
  gap: 6px;
}

.vs-slot {
  border: 1px solid rgb(var(--vs-primary-rgb) / 0.35);
  background: var(--vs-bg);
  color: var(--vs-primary);
  border-radius: 10px;
  padding: 7px 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 160ms,
    color 160ms;
}

.vs-slot:hover,
.vs-slot:focus-visible {
  background: var(--vs-primary);
  color: var(--vs-primary-fg);
}

.vs-rcards {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding-bottom: 4px;
  scrollbar-width: thin;
}

.vs-rcard {
  flex: 0 0 180px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border: 1px solid var(--vs-border);
  border-radius: 14px;
  background: var(--vs-bg);
  animation: vs-rise 420ms var(--vs-ease) both;
}

.vs-rcard-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--vs-fg);
}

.vs-rcard-sub {
  font-size: 12px;
  color: var(--vs-muted);
  flex: 1;
}

.vs-rcard-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 6px;
}

.vs-rcard-btn {
  border: none;
  background: var(--vs-primary);
  color: var(--vs-primary-fg);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.vs-rcard-link {
  font-size: 12px;
  color: var(--vs-primary);
  text-decoration: underline;
}

.vs-meeting {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding: 12px;
  border: 1px solid var(--vs-border);
  border-radius: 14px;
  background: var(--vs-bg);
}

.vs-meeting-title {
  font-weight: 600;
  font-size: 14px;
}

.vs-meeting-when {
  font-size: 13px;
  opacity: 0.8;
}

.vs-meeting-join {
  align-self: flex-start;
  margin-top: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: var(--vs-primary);
  color: var(--vs-primary-fg);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}

.vs-meeting-congrats {
  font-weight: 700;
  font-size: 15px;
}

.vs-meeting-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.vs-meeting-actions .vs-meeting-join {
  margin-top: 0;
}

.vs-meeting-copy {
  padding: 8px 14px;
  border: 1.5px solid var(--vs-border);
  border-radius: 999px;
  background: var(--vs-bg);
  color: var(--vs-fg);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.vs-home-new {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  margin: -4px 0 2px 4px;
  padding: 6px 10px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--vs-fg);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.vs-home-new:hover {
  background: var(--vs-primary-soft);
}

.vs-home-new:focus-visible {
  outline: 2px solid var(--vs-primary);
  outline-offset: 2px;
}

.vs-details-open {
  align-self: flex-start;
  margin-top: 8px;
  padding: 9px 14px;
  border: 1.5px solid var(--vs-primary);
  border-radius: 999px;
  background: var(--vs-primary-soft);
  color: var(--vs-fg);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.vs-sheet-backdrop {
  position: absolute;
  inset: 0;
  z-index: 30;
  /* Its own layer, so animated parts of the chat (iOS Safari) never paint over it. */
  transform: translateZ(0);
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgb(15 23 42 / 0.45);
}

.vs-sheet {
  width: 100%;
  max-width: 360px;
  margin: 0;
  border-radius: 20px;
  max-height: 100%;
  overflow-y: auto;
  box-shadow: 0 24px 60px -12px rgb(15 23 42 / 0.4);
}

/* The form inside the call screen: the orb steps aside to make room. */
.vs-voice.has-form .vs-orb,
.vs-voice.has-form .vs-voice-captions {
  display: none;
}

.vs-voice-form {
  width: 100%;
  margin: 0;
  color: var(--vs-fg);
  text-align: left;
}

.vs-sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.vs-sheet-close {
  border: 0;
  background: transparent;
  color: var(--vs-muted);
  font-size: 16px;
  cursor: pointer;
}

.vs-meeting-copy:focus-visible,
.vs-details-open:focus-visible,
.vs-sheet-close:focus-visible,
.vs-meeting-join:focus-visible,
.vs-slot:focus-visible,
.vs-slot-day:focus-visible,
.vs-rcard-btn:focus-visible,
.vs-rcard-link:focus-visible {
  outline: 2px solid var(--vs-primary);
  outline-offset: 2px;
}

.vs-csat-stars {
  display: flex;
  justify-content: center;
  gap: 2px;
  width: 100%;
}

.vs-csat-skip {
  border: 0;
  background: transparent;
  color: var(--vs-muted);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  text-decoration: underline;
}

.vs-csat-comment {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--vs-border);
  border-radius: 10px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  resize: none;
  margin: 4px 0 8px;
}

.vs-csat-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.vs-rate-chat {
  align-self: center;
  border: 0;
  background: transparent;
  color: var(--vs-muted);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  text-decoration: underline;
}

/* WhatsApp opt-in (unticked by default) and the "Continue on WhatsApp" card. */
.vs-optin {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 2px 0 10px;
  font-size: 12.5px;
  line-height: 1.4;
  color: inherit;
  cursor: pointer;
}
.vs-optin input {
  margin-top: 2px;
  width: 15px;
  height: 15px;
  accent-color: #25d366;
  flex-shrink: 0;
}
.vs-wa-card .vs-card-icon {
  background: #25d366;
  color: #fff;
}
